/**
 * AntImageUrl — Component quản lý URL 1 ảnh cho 1 field trên record.
 *
 * Props:
 *   value: string                  — URL hiện tại (full_path)
 *   onChange: function(url)        — callback khi URL thay đổi (set thành empty để xoá)
 *   voucherModule: string          — module name BE (ví dụ 'master')
 *   voucherType: string            — service name BE (ví dụ 'Item')
 *   voucherID: number              — id record hiện tại (BẮT BUỘC để BE update field)
 *   voucherCode: string (optional) — code record
 *   targetField: string            — tên field gửi BE (ví dụ 'avatar' / 'media_url_3')
 *   readOnly: boolean              — ẩn button upload + xoá
 *   label: string|object           — label hiển thị phía trên (i18n object {vi,en,...})
 *   width / height: number (px)    — kích thước drop-zone (mặc định 120)
 *
 * Upload BE: POST /{voucherModule}/{voucherType}/uploadAvatar
 *   formData: files[], code, target_field
 *   response: { rows: [{short_path, full_path, name, mime, size}] }
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import AntAPI from '../../utils/AntAPI';
import AntLocalization from '../../utils/AntLocalization';
import './index.scss';

export default function AntImageUrl(props) {
    var onChange = props.onChange;
    var readOnly = !!props.readOnly;
    var width = props.width || 120;
    var height = props.height || 120;

    var fileInputRef = useRef(null);
    var [value, setValue] = useState(props.value || '');
    var [uploading, setUploading] = useState(false);
    var [queuedGeneration, setQueuedGeneration] = useState(false);
    var [dragOver, setDragOver] = useState(false);
    var [errorMsg, setErrorMsg] = useState('');
    var serverGenerating = props.generating === true || props.generating === 1 || props.generating === '1';
    var wasServerGenerating = useRef(false);
    var isGenerating = serverGenerating || queuedGeneration;

    // Sync khi props.value đổi từ ngoài (vd: load record lần đầu, parent reset form)
    useEffect(function () {
        setValue(props.value || '');
    }, [props.value]);

    // Generation is asynchronous.  The optimistic state prevents the spinner
    // from disappearing immediately after enqueue; once updatePrepare has
    // reported a server state, only that canonical state controls the overlay.
    useEffect(function () {
        if (serverGenerating) {
            wasServerGenerating.current = true;
            setQueuedGeneration(true);
        } else if (wasServerGenerating.current || props.value) {
            setQueuedGeneration(false);
        }
    }, [serverGenerating, props.value]);

    useEffect(function () {
        if (!isGenerating || !props.onGenerateQueued) return undefined;
        var timer = setInterval(function () {
            props.onGenerateQueued();
        }, 3000);
        return function () { clearInterval(timer); };
    }, [isGenerating, props.onGenerateQueued]);

    var doUpload = useCallback(function (files) {
        if (!files || files.length === 0) return;
        if (!props.voucherID) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Vui lòng lưu sản phẩm trước khi tải ảnh',
                en: 'Please save the product before uploading images',
                fr: 'Enregistrez le produit avant d\'importer des images',
                es: 'Guarde el producto antes de subir imágenes',
                id: 'Simpan produk sebelum mengunggah gambar',
                ja: '画像をアップロードする前に商品を保存してください',
                zh: '请先保存商品再上传图片',
                ko: '이미지 업로드 전에 상품을 저장하세요',
                hi: 'चित्र अपलोड करने से पहले उत्पाद सहेजें',
                ar: 'يرجى حفظ المنتج قبل رفع الصور'
            }));
            return;
        }
        if (!props.voucherModule || !props.voucherType || !props.targetField) {
            setErrorMsg('Missing voucherModule / voucherType / targetField');
            return;
        }

        var file = files[0];
        if (!/^image\//.test(file.type)) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Chỉ chấp nhận tệp hình ảnh',
                en: 'Only image files are accepted',
                fr: 'Fichiers image uniquement',
                es: 'Solo se aceptan imágenes',
                id: 'Hanya menerima file gambar',
                ja: '画像ファイルのみ対応',
                zh: '仅支持图片文件',
                ko: '이미지 파일만 가능합니다',
                hi: 'केवल चित्र फ़ाइलें स्वीकार्य',
                ar: 'تُقبل ملفات الصور فقط'
            }));
            return;
        }

        setErrorMsg('');
        setUploading(true);

        var formData = new FormData();
        formData.append('files', file);
        formData.append('target_field', props.targetField);
        if (props.voucherCode) formData.append('code', props.voucherCode);

        var api = new AntAPI(props.voucherModule, props.voucherType);
        api.upload('/' + api.routeURL + '/uploadAvatar', formData, function (result) {
            setUploading(false);
            if (result && result.error) {
                setErrorMsg(result.message || AntLocalization.translateText({ vi: 'Tải ảnh thất bại', en: 'Upload failed', fr: 'Échec de l\'envoi', es: 'Error al subir', id: 'Gagal mengunggah', ja: 'アップロード失敗', zh: '上传失败', ko: '업로드 실패', hi: 'अपलोड विफल', ar: 'فشل الرفع' }));
                return;
            }
            // Response của uploadAvatar có dạng giống actionUpdatePrepare: { code, data: {full record} }
            // → set URL từ field tương ứng trên record, đồng thời chuyển result lên AntVoucher để
            // setMainData (component cha sẽ refresh form mà không cần gọi API lại).
            if (result && result.data) {
                var rec = result.data;
                var newUrl = '';
                if (props.targetField === 'avatar') newUrl = rec.avatar_full_path || '';
                else newUrl = rec[props.targetField] || '';
                setValue(newUrl);
                if (onChange) onChange(newUrl);
                if (props.onUploaded) props.onUploaded(result);
            }
        });
    }, [props.voucherID, props.voucherCode, props.voucherModule, props.voucherType, props.targetField, onChange, props.onUploaded]);

    var handleSelect = useCallback(function () {
        if (fileInputRef.current) fileInputRef.current.click();
    }, []);

    var handleFileChange = useCallback(function (e) {
        doUpload(e.target.files);
        e.target.value = ''; // reset để chọn lại file cũ vẫn trigger
    }, [doUpload]);

    var handleDragOver = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (!readOnly) setDragOver(true);
    }, [readOnly]);

    var handleDragLeave = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
    }, []);

    var handleDrop = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        setDragOver(false);
        if (readOnly) return;
        if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            doUpload(e.dataTransfer.files);
        }
    }, [readOnly, doUpload]);

    var handleClear = useCallback(function (e) {
        e.stopPropagation();
        setValue('');
        if (!onChange) return;
        // Clear cả short_path field cùng với dataField (full_path), vì 2 field này
        // luôn đi đôi (vd avatar_full_path + avatar_short_path). AntBaseForm sẽ chuyển
        // patch object xuống onFieldDataChanged → set multi field cùng lúc.
        // Convention: dataField=*_full_path → short field = *_short_path. Override
        // được qua props.shortPathField.
        var df = props.dataField || '';
        var shortField = props.shortPathField || (df.endsWith('_full_path') ? df.replace(/_full_path$/, '_short_path') : null);
        if (shortField) {
            var patch = {};
            patch[df] = '';
            patch[shortField] = '';
            onChange(patch);
        } else {
            onChange('');
        }
    }, [onChange, props.dataField, props.shortPathField]);

    // AI generate avatar — click magic wand → POST /{module}/{type}/{generateAction}.
    // Response giống actionUploadAvatar (updatePrepare: data full record). Set URL từ target_field.
    var handleGenerate = useCallback(function (e) {
        e.stopPropagation();
        if (!props.generateAction) return;
        if (readOnly || uploading || isGenerating) return;
        if (!props.voucherID) {
            setErrorMsg(AntLocalization.translateText({
                vi: 'Vui lòng lưu sản phẩm trước khi tạo ảnh AI',
                en: 'Please save the product before generating AI image',
                fr: 'Enregistrez le produit avant de générer l\'image IA',
                es: 'Guarde el producto antes de generar la imagen IA',
                id: 'Simpan produk sebelum membuat gambar AI',
                ja: 'AI画像を生成する前に商品を保存してください',
                zh: '请先保存商品再生成AI图片',
                ko: 'AI 이미지 생성 전에 상품을 저장하세요',
                hi: 'AI चित्र बनाने से पहले उत्पाद सहेजें',
                ar: 'يرجى حفظ المنتج قبل إنشاء صورة AI'
            }));
            return;
        }
        if (!props.voucherModule || !props.voucherType) {
            setErrorMsg('Missing voucherModule / voucherType');
            return;
        }

        setErrorMsg('');
        setUploading(true);

        var api = new AntAPI(props.voucherModule, props.voucherType);
        var body = { id: props.voucherID };
        if (props.voucherCode) body.code = props.voucherCode;
        api.post('/' + api.routeURL + '/' + props.generateAction, body, function (result) {
            setUploading(false);
            if (result && result.error) {
                setErrorMsg(result.message || AntLocalization.translateText({ vi: 'Tạo ảnh AI thất bại', en: 'AI generation failed', fr: 'Échec de la génération IA', es: 'Error al generar con IA', id: 'Gagal membuat gambar AI', ja: 'AI画像の生成に失敗', zh: 'AI图片生成失败', ko: 'AI 이미지 생성 실패', hi: 'AI चित्र बनाना विफल', ar: 'فشل إنشاء صورة AI' }));
                return;
            }
            if (result && result.data) {
                var rec = result.data;
                var newUrl = '';
                if (props.targetField === 'avatar') newUrl = rec.avatar_full_path || '';
                else newUrl = rec[props.targetField] || '';
                setValue(newUrl);
                if (onChange) onChange(newUrl);
                if (props.onUploaded) props.onUploaded(result);
            }
            // Queue actions return a job instead of an updated Item.  Mark the
            // image as generating immediately and let updatePrepare provide
            // the canonical `avatar_generating` value on the next polls.
            setQueuedGeneration(true);
            if (props.onGenerateQueued) props.onGenerateQueued();
        });
    }, [props.generateAction, props.voucherID, props.voucherModule, props.voucherType, props.targetField, readOnly, uploading, isGenerating, onChange, props.onUploaded, props.onGenerateQueued]);

    return (
        <div className="ant-image-url">
            <div
                className={'ant-image-url-box' + (dragOver ? ' is-drag' : '') + (value ? ' has-image' : '')}
                style={{ width: width, height: height }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={readOnly || uploading || isGenerating ? undefined : handleSelect}
            >
                {value ? (
                    <img className="ant-image-url-thumb" src={value} alt="" />
                ) : (
                    <div className="ant-image-url-empty">
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <rect x="3" y="3" width="18" height="18" rx="2" />
                            <circle cx="8.5" cy="8.5" r="1.5" />
                            <path d="M21 15l-5-5L5 21" />
                        </svg>
                        <div className="ant-image-url-empty-text">
                            {AntLocalization.translateText({
                                vi: 'Kéo ảnh vào đây hoặc click để chọn',
                                en: 'Drag image here or click to select',
                                fr: 'Glissez l\'image ici ou cliquez',
                                es: 'Arrastre la imagen aquí o haga clic',
                                id: 'Seret gambar ke sini atau klik',
                                ja: '画像をドラッグ、またはクリックして選択',
                                zh: '将图片拖到此处或点击选择',
                                ko: '이미지를 끌어오거나 클릭하여 선택',
                                hi: 'चित्र यहाँ खींचें या चुनने हेतु क्लिक करें',
                                ar: 'اسحب الصورة هنا أو انقر للاختيار'
                            })}
                        </div>
                    </div>
                )}

                {(uploading || isGenerating) && (
                    <div className="ant-image-url-overlay">
                        <div className="ant-image-url-spinner" />
                        <div>{AntLocalization.translateText(isGenerating ? { vi: 'Đang tạo ảnh...', en: 'Generating image...', fr: 'Génération...', es: 'Generando imagen...', id: 'Membuat gambar...', ja: '画像を生成中...', zh: '正在生成图片...', ko: '이미지 생성 중...', hi: 'चित्र बन रहा है...', ar: 'جارٍ إنشاء الصورة...' } : { vi: 'Đang tải...', en: 'Uploading...', fr: 'Envoi...', es: 'Subiendo...', id: 'Mengunggah...', ja: '送信中...', zh: '上传中...', ko: '업로드 중...', hi: 'अपलोड हो रहा...', ar: 'جارٍ الرفع...' })}</div>
                    </div>
                )}

                {!readOnly && !uploading && !isGenerating && value && (
                    <button className="ant-image-url-clear" onClick={handleClear} type="button" title={AntLocalization.translateText({ vi: 'Xoá ảnh', en: 'Remove', fr: 'Retirer', es: 'Quitar', id: 'Hapus', ja: '削除', zh: '删除', ko: '삭제', hi: 'हटाएँ', ar: 'حذف' })}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                    </button>
                )}

                {!readOnly && !uploading && !isGenerating && props.generateAction && (
                    <button
                        className="ant-image-url-generate"
                        onClick={handleGenerate}
                        type="button"
                        title={AntLocalization.translateText({ vi: 'Tạo ảnh bằng AI', en: 'Generate with AI', fr: 'Générer avec l\'IA', es: 'Generar con IA', id: 'Buat dengan AI', ja: 'AIで生成', zh: '用AI生成', ko: 'AI로 생성', hi: 'AI से बनाएँ', ar: 'إنشاء بالذكاء' })}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8L19 13M15 9h0M17.8 6.2L19 5M3 21l9-9M12.2 6.2L11 5" />
                        </svg>
                    </button>
                )}
            </div>

            {!readOnly && (
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            )}

            {errorMsg && <div className="ant-image-url-error">{errorMsg}</div>}
        </div>
    );
}
